/**
 * Search query language (Bear-inspired) shared by every client's note search.
 *
 * `parseSearchQuery` turns what the user typed into OR'd groups of AND'd terms:
 * text (`word`, `"a phrase"`), tags (`#tag`, `!#tag` without nested tags,
 * `#multi word#`) and specials (`@todo`, `@last7days`, `@date(>2026-10)` ...),
 * each optionally negated with a leading `-`. Anything that does not parse as
 * an operator is plain text, so a query never fails. Each client compiles the
 * result to SQL against its own database.
 *
 * `matchRangesForTerms` and `searchSnippet` show where a result matched.
 *
 * Mirrored 1:1 by Swift `SearchQuery.swift` (CaliuCore) and Kotlin
 * `SearchQuery.kt` (core); all three run `searchQuery.fixtures.json`.
 * Change all three or none.
 */
import { HASHTAG_BODY_PATTERN, HASHTAG_TOKEN_CLASS, normalizeHashtagBody } from "./hashtags"

export type TextTerm = { kind: "text"; text: string; phrase: boolean; negated: boolean }
export type TagTerm = { kind: "tag"; handle: string; nested: boolean; negated: boolean }
export type DateSpec = { cmp: "on" | "before" | "after"; y: number; m?: number; d?: number }
export type SpecialName =
  | "tagged" | "untagged" | "pinned" | "task" | "todo" | "done" | "images" | "files"
  | "attachments" | "code" | "wikilinks" | "readonly" | "untitled"
  | "modified" | "created"
/**
 * `modified` / `created` carry exactly one of: `days` (the last N local days,
 * today included: `@today` is 1), `yesterday`, or `date`.
 */
export type SpecialTerm = {
  kind: "special"
  name: SpecialName
  negated: boolean
  days?: number
  yesterday?: boolean
  date?: DateSpec
}
export type SearchTerm = TextTerm | TagTerm | SpecialTerm
export type QueryGroup = { terms: SearchTerm[]; titleOnly: boolean }
/** `highlights`: the positive text terms and `#tag`s, deduplicated case-insensitively. */
export type ParsedQuery = { groups: QueryGroup[]; highlights: string[] }

const FLAGS: Record<string, SpecialName> = {
  tagged: "tagged", untagged: "untagged", pinned: "pinned", task: "task", todo: "todo",
  done: "done", images: "images", files: "files", attachments: "attachments", code: "code",
  wikilinks: "wikilinks", readonly: "readonly", untitled: "untitled",
}

// A multi-word tag (`#my project#`) first; when that does not end the token
// (`#a -#b` reads as the multi-word body `a -`), the single-word form.
const TAG_AT_START = [
  new RegExp(`^#${HASHTAG_BODY_PATTERN}`, "u"),
  new RegExp(`^#(${HASHTAG_TOKEN_CLASS}+)#?`, "u"),
]

function isSpace(ch: string | undefined): boolean {
  return ch === " " || ch === "\t" || ch === "\n" || ch === "\r"
}

function isDigits(s: string): boolean {
  return s.length > 0 && [...s].every((c) => c >= "0" && c <= "9")
}

function daysInMonth(y: number, m: number): number {
  return [31, y % 4 === 0 && (y % 100 !== 0 || y % 400 === 0) ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][m - 1]
}

/** `2026-10-18`, `2026-10` or `2026`, optionally after `<` / `>`. */
function parseDate(arg: string): DateSpec | null {
  let cmp: DateSpec["cmp"] = "on"
  if (arg.startsWith("<")) cmp = "before"
  else if (arg.startsWith(">")) cmp = "after"
  const parts = (cmp === "on" ? arg : arg.slice(1)).split("-")
  if (parts.length > 3 || parts[0].length !== 4 || !parts.every(isDigits)) return null
  if (parts.slice(1).some((p) => p.length > 2)) return null
  const [y, m, d] = parts.map(Number)
  if (m !== undefined && (m < 1 || m > 12)) return null
  if (d !== undefined && (d < 1 || d > daysInMonth(y, m))) return null
  return { cmp, y, ...(m !== undefined && { m }), ...(d !== undefined && { d }) }
}

/** `@name` lowercased, without the `@`. `title` is handled by the caller. */
function parseSpecial(word: string, negated: boolean): SpecialTerm | null {
  const base = { kind: "special" as const, negated }
  if (FLAGS[word]) return { ...base, name: FLAGS[word] }
  if (word === "today") return { ...base, name: "modified", days: 1 }
  if (word === "ctoday") return { ...base, name: "created", days: 1 }
  if (word === "yesterday") return { ...base, name: "modified", yesterday: true }
  for (const [prefix, name] of [["last", "modified"], ["created", "created"]] as const) {
    if (word.startsWith(prefix) && word.endsWith("days")) {
      const n = word.slice(prefix.length, -4)
      if (isDigits(n) && n.length <= 4 && Number(n) > 0) return { ...base, name, days: Number(n) }
    }
  }
  for (const [prefix, name] of [["date(", "modified"], ["cdate(", "created"]] as const) {
    if (word.startsWith(prefix) && word.endsWith(")")) {
      const date = parseDate(word.slice(prefix.length, -1))
      if (date) return { ...base, name, date }
    }
  }
  return null
}

/** The tag starting at `s[hash]` (a `#`) when it ends at whitespace or the end. */
function readTag(s: string, hash: number): { handle: string; end: number } | null {
  for (const pattern of TAG_AT_START) {
    const match = pattern.exec(s.slice(hash))
    if (!match) continue
    const end = hash + match[0].length
    const handle = normalizeHashtagBody(match[1])
    if (handle && (end === s.length || isSpace(s[end]))) return { handle, end }
  }
  return null
}

/** Any of these opens or closes a phrase. */
const QUOTES = '"\u201C\u201D\u201E'

export function parseSearchQuery(input: string): ParsedQuery {
  const groups: QueryGroup[] = []
  let group: QueryGroup = { terms: [], titleOnly: false }
  const s = input
  let i = 0

  const readWord = (): string => {
    const start = i
    while (i < s.length && !isSpace(s[i])) i++
    return s.slice(start, i)
  }
  const pushText = (text: string, phrase: boolean, negated: boolean) => {
    if (text) group.terms.push({ kind: "text", text, phrase, negated })
  }

  while (i < s.length) {
    if (isSpace(s[i])) { i++; continue }
    const negated = s[i] === "-" && i + 1 < s.length && !isSpace(s[i + 1])
    if (negated) i++

    // Phrase, in straight or typographic quotes (iOS types “”). An unclosed
    // quote is dropped and the words after it read as plain words.
    if (QUOTES.includes(s[i])) {
      let close = i + 1
      while (close < s.length && !QUOTES.includes(s[close])) close++
      if (close === s.length) close = -1
      if (close === -1) {
        i++
        pushText(readWord(), false, negated)
        continue
      }
      pushText(s.slice(i + 1, close).split(/[ \t\n\r]+/).filter(Boolean).join(" "), true, negated)
      i = close + 1
      continue
    }

    // Tag: `#tag`, `!#tag`, `#multi word#`, ending at whitespace or the end.
    const exact = s[i] === "!" && s[i + 1] === "#"
    const hash = exact ? i + 1 : i
    const tag = s[hash] === "#" ? readTag(s, hash) : null
    if (tag) {
      group.terms.push({ kind: "tag", handle: tag.handle, nested: !exact, negated })
      i = tag.end
      continue
    }

    const word = readWord()
    if (word.startsWith("@")) {
      const name = word.slice(1).toLowerCase()
      if (name === "title") {
        if (!negated) group.titleOnly = true
        continue
      }
      const special = parseSpecial(name, negated)
      if (special) { group.terms.push(special); continue }
    }
    if (!negated && word.toLowerCase() === "or") {
      if (group.terms.length > 0) groups.push(group)
      group = { terms: [], titleOnly: false }
      continue
    }
    pushText(word, false, negated)
  }
  if (group.terms.length > 0) groups.push(group)

  const highlights: string[] = []
  const seen = new Set<string>()
  for (const term of groups.flatMap((g) => g.terms)) {
    if (term.negated || term.kind === "special") continue
    const text = term.kind === "tag" ? `#${term.handle}` : term.text
    if (seen.has(text.toLowerCase())) continue
    seen.add(text.toLowerCase())
    highlights.push(text)
  }
  return { groups, highlights }
}

/** Lowercase with diacritics removed (`Cafè` → `cafe`), one code point at a time. */
function foldCodePoint(cp: number): string {
  return String.fromCodePoint(cp).normalize("NFD").replace(/\p{M}/gu, "").toLowerCase()
}

/**
 * Every occurrence of any of `terms` in `text`, ignoring case and diacritics
 * (`cafe` marks `Café`, as the search folds them), as sorted, merged
 * `[start, end)` UTF-16 offsets. A range ends after the marks that follow it.
 */
export function matchRangesForTerms(text: string, terms: string[]): Array<[number, number]> {
  // `folded[k]` comes from the code point at `starts[k]`, which ends at `ends[k]`.
  let folded = ""
  const starts: number[] = []
  const ends: number[] = []
  for (let i = 0; i < text.length; ) {
    const cp = text.codePointAt(i)!
    const next = i + (cp > 0xffff ? 2 : 1)
    const f = foldCodePoint(cp)
    for (let k = 0; k < f.length; k++) {
      starts.push(i)
      ends.push(next)
    }
    folded += f
    i = next
  }
  const ranges: Array<[number, number]> = []
  for (const term of terms) {
    let t = ""
    for (const ch of term) t += foldCodePoint(ch.codePointAt(0)!)
    if (!t) continue
    for (let at = folded.indexOf(t); at !== -1; at = folded.indexOf(t, at + t.length)) {
      const last = at + t.length - 1
      const after = last + 1 < starts.length ? starts[last + 1] : text.length
      ranges.push([starts[at], Math.max(ends[last], after)])
    }
  }
  ranges.sort((a, b) => a[0] - b[0])
  const merged: Array<[number, number]> = []
  for (const r of ranges) {
    const last = merged[merged.length - 1]
    if (last && r[0] <= last[1]) last[1] = Math.max(last[1], r[1])
    else merged.push([r[0], r[1]])
  }
  return merged
}

/**
 * A window of `plain` (whitespace collapsed) around the first occurrence of any
 * of `terms`: up to 24 characters before it, starting at a word, with `…` in
 * front when cut. `null` when no term occurs.
 */
export function searchSnippet(plain: string, terms: string[], maxLength = 160): string | null {
  const clean = plain.split(/[ \t\n\r]+/).filter(Boolean).join(" ")
  const first = matchRangesForTerms(clean, terms)[0]
  if (!first) return null
  let start = Math.max(0, first[0] - 24)
  if (start > 0) {
    const space = clean.indexOf(" ", start)
    if (space !== -1 && space < first[0]) start = space + 1
  }
  if (/[\uDC00-\uDFFF]/.test(clean[start] ?? "")) start++
  let end = Math.min(clean.length, start + maxLength)
  // Never split a surrogate pair (an emoji) at the end either.
  if (end < clean.length && /[\uDC00-\uDFFF]/.test(clean[end])) end--
  const snippet = clean.slice(start, end)
  return start > 0 ? `…${snippet}` : snippet
}
